import { useEffect, useState } from "react";
import {
  SURF_LAYOUT_CATALOG,
  SURF_COMPOSITION_KINDS,
  applyLayoutToSlide,
  applyCompositionToSlide,
  applyDesignerOutputToDeck,
  arrangeSlideText,
  deleteSurfLayoutTemplate,
  effectiveThemeForSlide,
  generateSlideVisual,
  getSurfLayout,
  isComposedBackgroundMode,
  layersFromCustomLayoutSlots,
  listSurfLayoutTemplates,
  polishAllDeckSlides,
  polishDeckSlide,
  rewriteDeckSlidePromptsFromStyle,
  rewriteSingleSlidePromptFromStyle,
  runDesignerForAllSlides,
  runDesignerForSlide,
  saveSurfLayoutTemplate,
  slideToCustomLayoutSlots,
  type SurfCompositionKind,
  type SurfDeck,
  type SurfLayoutTemplateRecord,
} from "@vida/surf-core";
import {
  createSurfApiConfig,
  ensureSurfClientConfig,
} from "../../services/surfClientConfig";

type Props = {
  deck: SurfDeck;
  slideIndex: number;
  slideId: string;
  onDeckChange: (deck: SurfDeck) => void;
  onActivity: (msg: string | null) => void;
  disabled?: boolean;
};

export function EditorDesignPanel({
  deck,
  slideIndex,
  slideId,
  onDeckChange,
  onActivity,
  disabled,
}: Props) {
  const [layoutId, setLayoutId] = useState("title-body");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<SurfLayoutTemplateRecord[]>([]);
  const [templateTitle, setTemplateTitle] = useState("");

  const slide = deck.slides[slideIndex];

  const loadTemplates = async () => {
    await ensureSurfClientConfig();
    setTemplates(await listSurfLayoutTemplates(createSurfApiConfig()));
  };

  useEffect(() => {
    void loadTemplates().catch(() => undefined);
  }, []);

  const run = async (label: string, fn: () => Promise<void>) => {
    setError(null);
    setBusy(true);
    onActivity(label);
    try {
      await ensureSurfClientConfig();
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      onActivity(null);
    }
  };

  const applyManualLayout = () => {
    if (!slide) return;
    const layout = getSurfLayout(layoutId);
    if (!layout) return;
    const contents: Record<string, string> = {};
    for (const slot of layout.slots) {
      const existing = slide.layers.find(
        (l) => l.style?.slotRole === slot.role || l.textRole === slot.textRole,
      );
      contents[slot.role] = existing?.content ?? (slot.role === "image" ? "Immagine" : slot.role);
    }
    const nextSlide = applyLayoutToSlide(slide, layoutId, contents, {
      theme: deck.theme,
      autoArrange: true,
    });
    onDeckChange({
      ...deck,
      slides: deck.slides.map((s) => (s.id === slideId ? nextSlide : s)),
    });
  };

  return (
    <section className="editor-design-panel">
      <h3>Designer</h3>

      <label className="field-label">
        Layout catalogo
        <select value={layoutId} onChange={(e) => setLayoutId(e.target.value)} disabled={disabled || busy}>
          {SURF_LAYOUT_CATALOG.map((l) => (
            <option key={l.id} value={l.id}>
              {l.title}
            </option>
          ))}
        </select>
      </label>

      <div className="wizard-actions">
        <button type="button" disabled={disabled || busy} onClick={applyManualLayout}>
          Applica layout
        </button>
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() =>
            void run("Designer AI (slide)…", async () => {
              const cfg = createSurfApiConfig();
              const output = await runDesignerForSlide(cfg, deck, slideIndex);
              onDeckChange(applyDesignerOutputToDeck(deck, slideId, output));
            })
          }
        >
          Designer AI (slide)
        </button>
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() =>
            void run("Designer AI (tutte)…", async () => {
              const cfg = createSurfApiConfig();
              const next = await runDesignerForAllSlides(cfg, deck, (i, total) =>
                onActivity(`Designer slide ${i + 1}/${total}…`),
              );
              onDeckChange(next);
            })
          }
        >
          Designer AI (tutte)
        </button>
      </div>

      <h4>Contenuto</h4>
      <div className="wizard-actions">
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() =>
            void run("Rifinisci contenuto (slide)…", async () => {
              const cfg = createSurfApiConfig();
              const next = await polishDeckSlide(cfg, deck, slideIndex);
              onDeckChange(next);
            })
          }
        >
          Rifinisci contenuto (slide)
        </button>
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() =>
            void run("Rifinisci contenuto (tutte)…", async () => {
              const cfg = createSurfApiConfig();
              const next = await polishAllDeckSlides(cfg, deck, (i, total) =>
                onActivity(`Rifinisci slide ${i + 1}/${total}…`),
              );
              onDeckChange(next);
            })
          }
        >
          Rifinisci contenuto (tutte)
        </button>
      </div>

      {isComposedBackgroundMode(deck.theme) && slide && (
        <>
          <h4>Struttura</h4>
          <label className="field-label">
            Composizione slide
            <select
              value={slide.composition ?? "text-left"}
              disabled={disabled || busy}
              onChange={(e) => {
                const kind = e.target.value as SurfCompositionKind;
                void run("Aggiornamento struttura…", async () => {
                  let nextSlide = applyCompositionToSlide(slide, kind, deck.theme);
                  const slideTheme = effectiveThemeForSlide(deck.theme, nextSlide);
                  nextSlide = arrangeSlideText(nextSlide, slideTheme);
                  let nextDeck: SurfDeck = {
                    ...deck,
                    slides: deck.slides.map((s) => (s.id === slideId ? nextSlide : s)),
                  };
                  const cfg = createSurfApiConfig();
                  nextDeck = await generateSlideVisual(cfg, nextDeck, slideIndex);
                  onDeckChange(nextDeck);
                });
              }}
            >
              {SURF_COMPOSITION_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {kind}
                </option>
              ))}
            </select>
          </label>
        </>
      )}

      <h4>Prompt immagine da stile</h4>
      <div className="wizard-actions">
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() =>
            void run("Riscrivi prompt slide…", async () => {
              const cfg = createSurfApiConfig();
              const next = await rewriteSingleSlidePromptFromStyle(cfg, deck, slideId);
              onDeckChange(next);
            })
          }
        >
          Riscrivi prompt (questa slide)
        </button>
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() =>
            void run("Riscrivi prompt tutte…", async () => {
              const cfg = createSurfApiConfig();
              const next = await rewriteDeckSlidePromptsFromStyle(cfg, deck);
              onDeckChange(next);
            })
          }
        >
          Riscrivi prompt (tutte)
        </button>
      </div>

      <h4>Template layout</h4>
      <div className="template-save-row">
        <input
          type="text"
          placeholder="Nome layout"
          value={templateTitle}
          onChange={(e) => setTemplateTitle(e.target.value)}
          disabled={busy}
        />
        <button
          type="button"
          disabled={busy || !templateTitle.trim() || !slide}
          onClick={() =>
            void run("Salvataggio layout…", async () => {
              const slots = slideToCustomLayoutSlots(slide!);
              await saveSurfLayoutTemplate(
                createSurfApiConfig(),
                templateTitle.trim(),
                slots,
                slide?.layout,
              );
              setTemplateTitle("");
              await loadTemplates();
            })
          }
        >
          Salva slide come layout
        </button>
      </div>
      {templates.length > 0 && (
        <ul className="template-list">
          {templates.map((t) => (
            <li key={t.documentId}>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  if (!slide) return;
                  const contents: Record<string, string> = {};
                  for (const slot of t.slots) {
                    const ex = slide.layers.find((l) => l.style?.slotRole === slot.role);
                    contents[slot.role] = ex?.content ?? slot.role;
                  }
                  const filled = layersFromCustomLayoutSlots(t.slots, contents);
                  onDeckChange({
                    ...deck,
                    slides: deck.slides.map((s) =>
                      s.id === slideId
                        ? { ...s, layout: t.layoutId ?? "custom", layers: filled }
                        : s,
                    ),
                  });
                }}
              >
                Applica «{t.title}»
              </button>
              <button
                type="button"
                className="danger-link"
                disabled={busy}
                onClick={() =>
                  void run("Elimina…", async () => {
                    await deleteSurfLayoutTemplate(createSurfApiConfig(), t.documentId);
                    await loadTemplates();
                  })
                }
              >
                Elimina
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="error">{error}</p>}
    </section>
  );
}
